import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { inject, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { IgxDialogModule, IgxGridModule, IgxHierarchicalGridModule } from '@infragistics/igniteui-angular';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideHotToastConfig } from '@ngxpert/hot-toast';
import { IgxPieChartModule } from 'igniteui-angular-charts';
import { markedSmartypants } from 'marked-smartypants';
import { MarkdownModule, MarkdownModuleConfig, MARKED_EXTENSIONS, MARKED_OPTIONS, MarkedOptions, MarkedRenderer } from 'ngx-markdown';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ComponentsModule } from './components/components.module';
import { MaterialModule } from './core/material.module';
import { AuthGuard } from './core/security/guards/auth.guard';
import { DirectivesModule } from './directives/directives.module';
import { NumberPipe } from './pipes/sl-number.pipe';
import { EnvironmentService } from './services/environment.service';
import { HttpInterceptorProviders } from './services/interceptors/interceptors';
import { ViewsModule } from './views/views.module';

export function markedOptionsFactory(): MarkedOptions {
    const renderer = new MarkedRenderer();
    renderer.link = ({ href, text }): string =>
        `<a class="link" role="link" tabindex="0" target="_blank" rel="nofollow noopener noreferrer" href="${href}">${text}</a>`;

    return {
        renderer,
        gfm: true,
        breaks: false,
        pedantic: false
    };
}

const mdOptions: MarkdownModuleConfig = {
    markedOptions: {
        provide: MARKED_OPTIONS,
        useFactory: markedOptionsFactory
    },
    markedExtensions: [
        {
            provide: MARKED_EXTENSIONS,
            useFactory: markedSmartypants,
            multi: true
        }
    ]
};

@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        MaterialModule,
        IgxGridModule,
        IgxHierarchicalGridModule,
        IgxPieChartModule,
        IgxDialogModule,
        AppRoutingModule,
        MarkdownModule.forRoot(mdOptions),
        DirectivesModule,
        ViewsModule,
        ComponentsModule
    ],
    providers: [
        HttpInterceptorProviders,
        AuthGuard,
        NumberPipe,
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        provideTranslateService({
            loader: provideTranslateHttpLoader()
        }),
        provideHotToastConfig({ position: 'top-right', duration: 3000, dismissible: true, visibleToasts: 5 })
    ]
})
export class AppModule {
    private readonly environmentService = inject(EnvironmentService);

    constructor() {
        this.environmentService.updateTenant();
    }
}
